import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { ThemeToggle } from '@/components/ThemeToggle';
import logo from '@/assets/logo.png';

export default function Terms() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Terms of Service - Legal Terms & Conditions | Sentinel"
        description="Read Sentinel's terms of service. Understand your rights and responsibilities when using our financial reconciliation platform."
      />
      
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border/50 bg-background/95 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src={logo} alt="SENTINEL Logo" className="w-10 h-10 brightness-0 dark:brightness-100 dark:invert" />
                <span className="text-2xl font-bold">SENTINEL</span>
              </div>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button onClick={() => navigate('/auth')}>Sign In</Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-6 py-20 max-w-4xl">
          <h1 className="text-5xl font-bold mb-6">Terms of Service</h1>
          <p className="text-muted-foreground mb-12">Last updated: January 2025</p>

          <Card className="p-8 mb-8 border-border/50">
            <div className="prose prose-slate dark:prose-invert max-w-none space-y-6">
              <section>
                <h2 className="text-3xl font-bold mb-4">1. Acceptance of Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing and using Sentinel ("Service"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">2. Description of Service</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Sentinel provides AI-powered financial reconciliation, invoice analysis, fraud detection, and related financial management services. The Service includes:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Automated bank transaction reconciliation</li>
                  <li>Invoice scanning and duplicate detection</li>
                  <li>Tally ERP integration and synchronization</li>
                  <li>AI-powered fraud detection and risk analysis</li>
                  <li>Cash flow forecasting and reporting</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">3. User Accounts</h2>
                <h3 className="text-xl font-semibold mb-3">3.1 Registration</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  To use the Service, you must create an account by providing accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials.
                </p>

                <h3 className="text-xl font-semibold mb-3">3.2 Account Security</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>You are responsible for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Use strong passwords and enable two-factor authentication when available</li>
                  <li>Do not share your account credentials with others</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">4. Subscription and Payment</h2>
                <h3 className="text-xl font-semibold mb-3">4.1 Plans and Pricing</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Sentinel offers Free, Pro, and Enterprise plans. Current pricing is available on our website and may be changed with 30 days' notice.
                </p>

                <h3 className="text-xl font-semibold mb-3">4.2 Billing</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Subscriptions are billed monthly or annually in advance</li>
                  <li>All fees are non-refundable except as required by law</li>
                  <li>Failure to pay may result in service suspension or termination</li>
                  <li>You authorize us to charge your payment method automatically</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3">4.3 Cancellation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You may cancel your subscription at any time. Cancellation takes effect at the end of your current billing period. No refunds for partial months.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">5. Acceptable Use</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">You agree not to:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Violate any laws or regulations</li>
                  <li>Infringe on intellectual property rights</li>
                  <li>Transmit malware, viruses, or harmful code</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Reverse engineer or decompile the Service</li>
                  <li>Use the Service to compete with us</li>
                  <li>Share your account with unauthorized users</li>
                  <li>Scrape or harvest data from the Service</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">6. Data and Intellectual Property</h2>
                <h3 className="text-xl font-semibold mb-3">6.1 Your Data</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  You retain all rights to your financial data. By using the Service, you grant us a license to process, store, and analyze your data to provide the Service. We do not claim ownership of your data.
                </p>

                <h3 className="text-xl font-semibold mb-3">6.2 Our IP</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The Service, including its design, features, algorithms, and documentation, is our intellectual property. You may not copy, modify, or distribute any part of the Service without our written permission.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">7. Service Availability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We strive for 99.9% uptime but do not guarantee uninterrupted service. We may perform maintenance that temporarily limits functionality. We are not liable for any damages resulting from service interruptions.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">8. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  To the maximum extent permitted by law:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>The Service is provided "as is" without warranties of any kind</li>
                  <li>We are not liable for indirect, incidental, or consequential damages</li>
                  <li>Our total liability shall not exceed the fees paid by you in the past 12 months</li>
                  <li>We are not responsible for third-party services or integrations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">9. Indemnification</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the Service, violation of these Terms, or infringement of any rights of others.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">10. Termination</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We may suspend or terminate your account if you:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Violate these Terms</li>
                  <li>Fail to pay fees</li>
                  <li>Engage in fraudulent or illegal activity</li>
                  <li>Pose a security risk to our systems or other users</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Upon termination, you lose access to the Service and your data may be deleted after a grace period.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">11. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms are governed by the laws of India. Any disputes shall be resolved in the courts of [Your City], India.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">12. Changes to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may modify these Terms at any time. Material changes will be notified via email or in-app notification. Continued use after changes indicates acceptance.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">13. Contact</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For questions about these Terms, contact us at:
                </p>
                <div className="mt-4 p-4 bg-surface rounded-lg">
                  <p className="font-medium mb-2">Email: legal@sentinel.com</p>
                  <p className="text-muted-foreground">Response time: Within 5 business days</p>
                </div>
              </section>
            </div>
          </Card>
        </div>

        <footer className="border-t border-border/50 py-8 bg-surface/30">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 SENTINEL. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
